import { useLayoutEffect, useRef, useState } from 'react';

import flatten from 'lodash/fp/flatten';
import flow from 'lodash/fp/flow';
import times from 'lodash/fp/times';
import zipWith from 'lodash/fp/zipWith';

import { theme } from '@prose-ui';
import { css, legacyTheme, styled } from '@prose-ui/legacy';

import { CTA } from '../Apps/LandingPages/Scenes/StylingGel/CTA';

import { Typography } from 'Components/Typography';

import {
  coilyTestimonialsContent as coilyContent,
  curlTestimonialsContent as curlContent,
  testimonialsContent as content,
} from 'assets/content/landingPages/stylingGel';

import useResponsiveVariant from 'utils/useResponsiveVariant';
/**
 * ct: column track
 * rt: row track
 * --root-padding: shared with root children
 */
const MobileRoot = styled.div`
  --root-padding: 32px;
  --content-max-width: 500px;

  padding: var(--root-padding, 32px);

  display: flex;
  flex-direction: column;
  justify-items: center;
  gap: 20px;

  background-color: ${theme.colors.neutral[200]};
`;

const DesktopRoot = styled.div`
  --root-padding: 72px;

  display: grid;

  grid-template-columns: [ct1] minmax(377px, 1440px) [ct2] minmax(377px, 1440px) [ct3];
  grid-template-rows: [rt1] 1fr [rt2] 3fr [rt3];

  justify-items: center;

  background-color: ${theme.colors.neutral[200]};
`;

const TitleWrapper = styled.div<{ wideScreenOrder: WideScreenOrder }>`
  max-width: 100%;

  grid-column: ${(props) => (props.wideScreenOrder === '12' ? 'ct2 / ct3' : 'ct1 / ct2')};
  grid-row: rt1 / rt2;

  ${legacyTheme.breakpoints.up('sm')} {
    align-self: end;

    width: 100%;
    max-width: var(--content-max-width, 500px);
    margin-inline: var(--root-padding, auto);
    padding-block-start: var(--root-padding, 32px);
  }
`;

const Underline = styled.div`
  display: none;
  ${legacyTheme.breakpoints.up('sm')} {
    display: revert;

    width: 100%;
    height: 1px;
    margin-block: 20px;

    background-color: ${theme.colors.neutral[500]};
  }
`;

const ImageSliderWrapper = styled.picture`
  display: block;
  width: 100%;
  height: 100%;

  flex: 1 0 auto;

  scroll-snap-align: center;
  scroll-snap-stop: always;
`;

const Image = styled.img`
  display: block;
  max-width: 100%;

  ${'' /* the image should take the whole height up mobile */}
  ${legacyTheme.breakpoints.up('sm')} {
    height: 100%;
  }

  object-fit: cover;
  object-position: center center;
`;

const CarouselWrapper = styled.div<{ wideScreenOrder: WideScreenOrder }>`
  grid-column: ${(props) => (props.wideScreenOrder === '12' ? 'ct1 / ct2' : 'ct2 / ct3')};
  grid-row: rt1 / rt3;

  max-width: 100%;

  ${
    '' /* enable to center carousel child in a responsive way
  margin instead of padding because with padding we would see other carousel items */
  }
  margin-inline: calc((100% - 314px) / 2);

  ${legacyTheme.breakpoints.up('sm')} {
    margin-inline: calc((100% - 384px) / 2);
  }

  ${legacyTheme.breakpoints.up('md')} {
    margin-inline: calc((100% - 720px) / 2);
  }

  ${legacyTheme.breakpoints.up('lg')} {
    margin-inline: calc((100% - 1440px) / 2);
  }
`;

const SlideContainer = styled.div`
  height: 100%;

  display: flex;
  flex-wrap: nowrap;
  column-gap: var(--root-padding, 32px);

  overflow-x: scroll;
  scroll-snap-type: x mandatory;

  @supports (scrollbar-width: none) {
    scrollbar-width: none;
  }

  &::-webkit-scrollbar {
    display: none;
  }
`;

const ContentSliderContainer = styled.div<{ wideScreenOrder: WideScreenOrder }>`
  grid-column: ${(props) => (props.wideScreenOrder === '12' ? 'ct2 / ct3' : 'ct1 / ct2')};
  grid-row: rt2 / rt3;

  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 20px;

  ${legacyTheme.breakpoints.up('sm')} {
    max-width: var(--content-max-width, 500px);
    margin-inline: var(--root-padding, auto);
    padding-block-end: var(--root-padding, 32px);
  }
`;

const TabContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const FlexChildWrapper = styled.div`
  flex: 1 1 0;
`;

/* removes default styles from button tag */
const TabButton = styled(Typography)`
  border: none;
  background: none;
  cursor: pointer;
`;

const InlineContainer = styled.div`
  & > * {
    display: inline;
  }
`;

/**
 * Utility to hide visually in a display none way but keep it accessible
 * https://css-tricks.com/places-its-tempting-to-use-display-none-but-dont/
 */
const visuallyHidden = css`
  position: absolute;
  overflow: hidden;
  clip: rect(0 0 0 0);
  height: 1px;
  width: 1px;
  margin: -1px;
  padding: 0;
  border: 0;
`;

type WideScreenOrder = '12' | '21';

type StylinGelTestimoniesProps = {
  variant?: 'curl' | 'coily' | null;
  wideScreenOrder?: WideScreenOrder;
};

export const StylingGelTestimonies = ({
  variant = null,
  wideScreenOrder = '12',
}: StylinGelTestimoniesProps) => {
  const { isMobile, isDesktop } = useResponsiveVariant();
  // used to track the tab/slide to show
  const [tabState, setTabState] = useState(0);
  // needed to know to which slide we should scroll to
  const slidesRef = useRef<Map<number, HTMLElement> | null>(null);
  // needed to attach/detach the scroll event listener
  const slideContainerRef = useRef(null);
  // slide observer to know when to display a different tab
  const slideObserverRef = useRef<IntersectionObserver | null>(null);

  const activeContent =
    variant === 'coily' ? coilyContent : variant === 'curl' ? curlContent : content;
  const { title, images, legends, testimonies, proTip, productsUsed } = activeContent;

  /**
   * technique seen on
   * https://beta.reactjs.org/learn/manipulating-the-dom-with-refs#example-scrolling-to-an-element
   */
  function getMap() {
    if (!slidesRef.current) {
      // Initialize the Map on first usage.
      slidesRef.current = new Map();
    }
    return slidesRef.current;
  }

  // Slide observer initialization
  useLayoutEffect(() => {
    if (slideContainerRef.current !== null) {
      slideObserverRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // access to the data attribute list of the element
              const slideIndex = parseInt(
                (entry.target as HTMLElement).dataset.slideIndex || '',
                10,
              );
              setTabState(slideIndex);
            }
          });
        },
        { root: slideContainerRef.current, threshold: 0.1 },
      );

      const slideMap = getMap();
      slideMap.forEach((slide) => {
        slideObserverRef.current?.observe(slide);
      });

      return () => {
        slideObserverRef.current?.disconnect();
      };
    }
    return () => {};
  }, []);

  function scrollToId(itemId: number) {
    const map = getMap();
    const node = map.get(itemId);
    node?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }

  const Root = isMobile ? MobileRoot : DesktopRoot;
  return (
    <Root>
      <TitleWrapper wideScreenOrder={wideScreenOrder}>
        <Typography align="left" variant="h1r-mono">
          {title}
        </Typography>
        <Underline />
      </TitleWrapper>
      <CarouselWrapper wideScreenOrder={wideScreenOrder}>
        <SlideContainer ref={slideContainerRef}>
          {images.map(({ alt, mobile, tablet, desktop }, key) => (
            <ImageSliderWrapper
              key={alt}
              ref={(node) => {
                const map = getMap();
                if (node) {
                  map.set(key, node);
                } else {
                  map.delete(key);
                }
              }}
              data-slide-index={key}
            >
              <source media="(max-width: 768px)" srcSet={mobile.image.src} />
              <source media="(max-width: 1024px)" srcSet={tablet.image.src} />
              <source media="(min-width: 1025px)" srcSet={desktop.image.src} />
              <Image
                alt={alt}
                srcSet={`${mobile.image.src} ${mobile.width}w,
                    ${tablet.image.src} ${tablet.width}w,
                    ${desktop.image.src} ${desktop.width}w`}
              />
            </ImageSliderWrapper>
          ))}
        </SlideContainer>
      </CarouselWrapper>
      <ContentSliderContainer wideScreenOrder={wideScreenOrder}>
        <TabContainer>
          {/* @ts-expect-error lodash function to be migrated */}
          {flow(
            zipWith(
              (space, tab) => (space ? [tab, space] : [tab]),
              times((n) => (
                <FlexChildWrapper key={String(n + 10)}>
                  <div />
                </FlexChildWrapper>
              ))(2),
            ),
            flatten,
          )(
            legends.map((legend, key) => (
              <FlexChildWrapper key={legend.text}>
                <TabButton
                  align="left"
                  bold
                  color={tabState === key ? 'noir' : 'grey'}
                  data-click={`slide-${key + 1}-clicked`}
                  data-from="testimonials-module"
                  markupName="button"
                  onClick={() => scrollToId(key)}
                  underline={tabState === key}
                  variant="mono2"
                >
                  {legend.text}
                </TabButton>
              </FlexChildWrapper>
            )),
          )}
        </TabContainer>

        {testimonies.map((testimony, key) => (
          <div key={testimony} css={tabState !== key ? visuallyHidden : undefined}>
            <Typography variant="p1">{testimony}</Typography>
          </div>
        ))}
        <InlineContainer>
          <Typography bold variant="mono1">
            {proTip.title}
          </Typography>
          {proTip.text.map((text, key) => (
            <Typography key={text} css={tabState !== key ? visuallyHidden : undefined} variant="p1">
              {text}
            </Typography>
          ))}
        </InlineContainer>
        <InlineContainer>
          <Typography bold variant="mono1">
            {productsUsed.title}
          </Typography>
          {productsUsed.text.map((text, key) => (
            <Typography key={text} css={tabState !== key ? visuallyHidden : undefined} variant="p1">
              {text}
            </Typography>
          ))}
        </InlineContainer>
        {isDesktop && (
          <CTA dataFrom="testimonials-module" fullWidth variant="vert">
            Define your style
          </CTA>
        )}
      </ContentSliderContainer>
    </Root>
  );
};
